import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type {
  PendingActivationAccountsDto,
  StalePendingActivationsDto,
} from '@/api/admin-activation-alerts-client';
import { useAdminActivationAlertsClient } from '@/api/admin-activation-alerts-client-context';

// =============================================================================
// use-admin-activation-alerts.ts — Hook TanStack Query de l'alerte 48h (§2.2 l.728).
// =============================================================================
// Une seule lecture (useQuery) — aucune mutation (admin LIT, jamais d'écriture, invariant n°1). Le
// client effectif vient du AdminActivationAlertsClientProvider (doublable, stub-first). ZÉRO règle
// métier ici : l'autorité (RBAC AdminSessionGuard, calcul AU VOL du compte) est l'API.

const ADMIN_ACTIVATION_ALERTS_KEY = ['admin-activation-alerts'] as const;
const ADMIN_PENDING_ACCOUNTS_KEY = [
  'admin-activation-alerts',
  'pending-accounts',
] as const;

/**
 * (§2.2 l.728) Compte des comptes en attente d'activation au-delà du seuil (défaut 48h). Rendu auto au
 * chargement du dashboard admin ; l'UI n'affiche l'alerte que si le compte est strictement positif.
 */
export function useAdminActivationAlerts(): UseQueryResult<
  StalePendingActivationsDto,
  Error
> {
  const client = useAdminActivationAlertsClient();
  return useQuery<StalePendingActivationsDto, Error>({
    queryKey: ADMIN_ACTIVATION_ALERTS_KEY,
    queryFn: () => client.getStalePendingActivations(),
  });
}

/**
 * (§2.5 l.1085, item 7) TABLEAU NOMINATIF des comptes en attente d'activation (token EXPIRÉ OU émis depuis
 * > seuil). Rendu dans la Zone alertes du dashboard admin ; l'UI n'affiche le tableau que si au moins une
 * ligne est présente. ZÉRO règle métier ici : l'autorité (RBAC, critère) est l'API.
 */
export function useAdminPendingActivations(): UseQueryResult<
  PendingActivationAccountsDto,
  Error
> {
  const client = useAdminActivationAlertsClient();
  return useQuery<PendingActivationAccountsDto, Error>({
    queryKey: ADMIN_PENDING_ACCOUNTS_KEY,
    queryFn: () => client.getPendingActivationAccounts(),
  });
}
